import { IBasicSubscription } from '@sdk/contracts';
import { Flex } from 'antd';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useTheme } from 'styled-components';

import {
  SubscriptionCardWrapper,
  CourseInfoWrapper,
  CourseName,
  CourseProgressWrapper,
  EmojiWrapper,
  ProgressBarWrapper,
  ProgressText
} from './SubscriptionCard.styles';

import { Icon } from '@/components/Icon';
import { MultiSegmentProgress } from '@/components/MultiSegmentProgressCompat';
import { useIsMobile } from '@/hooks';
import { useMySubscriptions } from '@/store';

type Props = {
  subscription: IBasicSubscription;
};

export const SubscriptionCard: React.FC<Props> = ({ subscription }) => {
  const { colors } = useTheme();
  const { setSelectedSubscription, setIsOpen } = useMySubscriptions();
  const navigateTo = useNavigate();
  const isMobile = useIsMobile();
  const handleSelectSubscription = () => {
    setSelectedSubscription(subscription);
    navigateTo(`/subscription/summary?activeTab=course-content`);
    setIsOpen(false);
  };

  const { t } = useTranslation('StudentDashboardTranslations');

  const progressText = isMobile
    ? `${subscription.progress}%`
    : `${subscription.progress}% ${t('COMPLETED').toLocaleLowerCase()}`;

  const isProgressStarted = useMemo(() => subscription.progress > 0, [subscription.progress]);

  return (
    <SubscriptionCardWrapper onClick={handleSelectSubscription}>
      <Flex gap={16} align="center" justify="center">
        <EmojiWrapper>
          <span role="img" aria-label={`${subscription.course.name} emoji`}>
            {subscription.course.emoji}
          </span>
        </EmojiWrapper>
        <CourseInfoWrapper>
          <CourseName title={subscription.course.name}>{subscription.course.name}</CourseName>
          <CourseProgressWrapper>
            <ProgressBarWrapper>
              <MultiSegmentProgress
                segments={[
                  {
                    id: 'progress',
                    percentage: subscription.progress || 0,
                    color: colors.green6
                  }
                ]}
                showTooltips={false}
                height={8}
                borderRadius={8}
                backgroundColor={colors.black3}
              />
            </ProgressBarWrapper>
            <ProgressText $isProgressStarted={isProgressStarted}>{progressText}</ProgressText>
          </CourseProgressWrapper>
        </CourseInfoWrapper>
      </Flex>

      {!isMobile && <Icon i="ArrowRight" color="black8" size={20} />}
    </SubscriptionCardWrapper>
  );
};
